import { TCurrentView } from "@components/RoundColumn";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import useClientContext from "@hooks/useClientContext";
import useGame from "@hooks/useGame";
import usePicksContext, { formatPicks, Slot } from "@hooks/usePicksContext";
import usePrevious from "@hooks/usePrevious";
import useSendPickMutation from "@hooks/useSendPickMutation";
import { PlayoffsEntry } from "@hooks/useSession";
import React, { useEffect, useState } from "react";
import DriverSlot from "../DriverSlot";
import {
  ColContainer,
  Connector,
  ControlBar,
  DriverList,
  LockMessage,
  NCSLogoArea,
  RaceList,
  //   ResultContainer,
  //   ScoringButton,
  RoundFooter,
  //   SaveButton,
  RoundWrapper,
  SeparatorSpan,
  TrophyArea,
} from "../RoundColumn.styled";
import TwelveSVG from "../svgs/12-round-marque.svg";
import SixteenSVG from "../svgs/16-round-marque.svg";
import FourSVG from "../svgs/4-round-marque.svg";
import EightSVG from "../svgs/8-round-marque.svg";
import UnlockSVG from "../svgs/lock-open.svg";
import LockSVG from "../svgs/lock.svg";
import NCSBadgeSVG from "../svgs/ncs-logo.svg";
import TrophySVG from "../svgs/ncs-trophy.svg";

interface Props {
  roundId: number;
  isTabletCeil: boolean;
  leagueMemberEntry: PlayoffsEntry;
}

function MemberRoundColumn({ roundId, isTabletCeil, leagueMemberEntry }: Props) {
  const { data: game } = useGame();
  const [{ currentEntry }] = useClientContext();
  const [{ focusedColumn }] = usePicksContext();
  const [currentView, setCurrentView] = useState<TCurrentView>("viewing");

  const entryId = currentEntry?.id;
  const round = game.rounds.find((r) => r.round_id === roundId);
  const nextRound = game.rounds.find((r) => r.round_id === roundId + 1);
  const raceNames = nextRound?.races?.map((id) => game?.races?.[id]?.track_name);

  const qualifyingDrivers: Slot[] = game.drivers?.map((d, i) => ({
    id: `${i + 1}`,
    pick: d.driver_id,
  }));

  const now = Date.now();
  const isCurrentRound = round?.round_id === game.state?.current_round ?? false;
  const isPostLock = now > round?.locks_at ?? false;
  const isPreOpen = now < round?.starts_at ?? false;
  const isOpen = (!isPostLock && now >= round?.starts_at && isCurrentRound) ?? false;
  const isChampionshipRound = roundId === 4;

  const isCollapsed =
    isTabletCeil && (roundId === focusedColumn - 1 || (focusedColumn === 0 && roundId === 1));
  const isHidden =
    isTabletCeil &&
    (focusedColumn === 0 ? roundId > 1 : roundId > focusedColumn || roundId < focusedColumn - 1);

  // const now = Date.parse("10/5/2021 11:00 AM"); // mock a custom "now" date
  // const isOpen = (!isPostLock && now >= round?.starts_at) ?? false; // mock a custom "now" date

  /**
   * Format: Wednesday, September 1, 2021
   */
  const getLocaleString = (iso?: number): string =>
    new Date(iso || Date.now()).toLocaleString(["default"], {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "America/New_York",
    });

  /**
   * Format: Monday, Sep 20 @ 2:55 PM EDT
   */
  const getLockLocaleString = (iso?: number): string => {
    let commaPointer = 0;
    return new Date(iso || Date.now())
      .toLocaleString(["default"], {
        weekday: "long",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        timeZone: "America/New_York",
        timeZoneName: "short",
      })
      .replace(/,/g, (match) => {
        commaPointer++;
        return commaPointer === 2 ? " @" : match;
      });
  };
  const openLocaleString = getLocaleString(round?.starts_at);
  const lockLocaleString = getLockLocaleString(round?.locks_at);

  const nextRoundIsOpen = (now >= nextRound?.starts_at && now < nextRound?.locks_at) ?? false;

  const nullDrivers: Slot[] = [];
  for (let i = 0; i < (round?.slots ?? 16); i++) {
    nullDrivers.push({ id: `${i + 1}`, pick: null });
  }

  // check if leagueMemberEntry exists, if so format picks to application state, otherwise set to null.
  const leagueMemberDrivers = leagueMemberEntry ? formatPicks(leagueMemberEntry["picks"]) : null;

  const drivers: Slot[] =
    (roundId > 0 && leagueMemberDrivers ? leagueMemberDrivers[roundId] : qualifyingDrivers) ||
    nullDrivers;

  const { isError, reset } = useSendPickMutation();
  const wasError = usePrevious(isError);

  // Reset the error message on next render cycle.
  useEffect(() => {
    if (wasError) reset();
  }, [reset, wasError]);

  useEffect(() => {
    setCurrentView("viewing");
  }, [entryId]);

  return (
    !isHidden && (
      <SortableContext items={drivers} strategy={verticalListSortingStrategy}>
        <ColContainer isCollapsed={isCollapsed} roundId={roundId}>
          <RaceList roundId={roundId}>
            {isTabletCeil && (
              <>
                {roundId === 0 && <SixteenSVG height="30" />}
                {roundId === 1 && <TwelveSVG height="30" />}
                {roundId === 2 && <EightSVG height="30" />}
                {roundId === 3 && <FourSVG height="30" />}
              </>
            )}
            {!isCollapsed &&
              raceNames?.map((name, i) => (
                <React.Fragment key={`${i}-name`}>
                  {name}
                  <SeparatorSpan />
                </React.Fragment>
              ))}
          </RaceList>
          <RoundWrapper roundId={roundId}>
            {isChampionshipRound && (
              <TrophyArea>
                <TrophySVG />
              </TrophyArea>
            )}
            <Connector isOpen={isOpen} roundId={roundId} isCurrentRound={isCurrentRound} />
            <DriverList roundId={roundId} isCurrentRound={isCurrentRound}>
              {roundId > 0 && (
                <ControlBar>
                  {isOpen && (
                    <LockMessage isOpen={isOpen}>
                      <UnlockSVG width="12" height="12" alt="Unlocked icon" />
                      <span>Locks {lockLocaleString}</span>
                    </LockMessage>
                  )}
                  {isPreOpen && (
                    <LockMessage>
                      <LockSVG width="12" height="12" alt="Lock icon" />
                      <span>Opens {openLocaleString}</span>
                    </LockMessage>
                  )}
                </ControlBar>
              )}
              {drivers?.map((slot, index) => {
                return (
                  <DriverSlot
                    key={slot.id}
                    id={slot.id}
                    slotNumber={index + 1}
                    driverId={slot.pick}
                    currentView={currentView}
                    isEditable={isOpen}
                    isPickable={nextRoundIsOpen}
                    hasScoring={false}
                    roundId={roundId}
                    isCollapsed={isCollapsed}
                    points={slot.points ? slot.points : null}
                  />
                );
              })}
            </DriverList>
            {isChampionshipRound && (
              <NCSLogoArea>
                <NCSBadgeSVG />
              </NCSLogoArea>
            )}
          </RoundWrapper>
          <RoundFooter>
            {roundId === 0 && <SixteenSVG width="58" />}
            {roundId === 1 && <TwelveSVG width="58" />}
            {roundId === 2 && <EightSVG width="58" />}
            {roundId === 3 && <FourSVG width="58" />}
          </RoundFooter>
        </ColContainer>
      </SortableContext>
    )
  );
}

export default MemberRoundColumn;
